// components/TiffProcessor.jsx
import { useEffect, useState, useCallback } from "react";
import L from "leaflet";
import useTiffProcessor, {
  colorScaleCU,
  colorScaleSPI,
} from "../../hooks/Tiff";
import useRasters from "../../hooks/useRasters";
import GeoTIFF from "geotiff";

const TiffProcessor = ({ map, startDate, type }) => {
  const [year, month] = startDate.split("-");
  const formattedMonth = String(Number(month)).padStart(2, "0");
  const period = `${year}-${formattedMonth}`;

  const { hasPeriod, loading: rastersLoading } = useRasters();

  // Served by the backend off a mounted directory instead of being baked into
  // the SPA build, so a newly added file is visible without a rebuild. The
  // backend maps the type to its subdirectory, so no directory name is built
  // here any more.
  const available = ["SPI", "CU", "SPI-3M"].includes(type) && hasPeriod(type, period);
  const tiffFile = available ? `panel/rasters/${type}/${period}.tif` : null;

  const { imageUrl, imageBounds, hoverValue, hoverLatLng, handleMouseMove } =
    useTiffProcessor(tiffFile, type);
  const [opacity, setOpacity] = useState(1);

  useEffect(() => {
    if (imageUrl && imageBounds && map) {
      const overlay = L.imageOverlay(imageUrl, imageBounds, { opacity }).addTo(
        map
      );
      map.on("mousemove", (e) => handleMouseMove(e, map));
      return () => {
        map.removeLayer(overlay);
        map.off("mousemove", handleMouseMove);
      };
    }
  }, [imageUrl, imageBounds, map, handleMouseMove, opacity]);

  useEffect(() => {
    if (!map) return;
    const scale = type === "CU" ? colorScaleCU : colorScaleSPI;

    const legend = L.control({ position: "bottomright" });
    legend.onAdd = () => {
      const div = L.DomUtil.create("div", "info legend flex flex-row");
      div.style = `
                display: flex;
                border: 1px solid #ccc;
                background: rgba(255,255,255,0.8);
                border-radius: 8px;
                overflow: hidden;
            `;

      scale.forEach(({ value, color }) => {
        const [r, g, b] = color;
        const textColor =
          r * 0.299 + g * 0.587 + b * 0.114 > 186 ? "#000" : "#fff";
        const block = L.DomUtil.create("div", "", div);
        block.style = `
                    width: 30px;
                    height: 30px;
                    background: rgb(${r},${g},${b});
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 10px;
                    color: ${textColor};
                `;
        block.innerText = value;
      });
      return div;
    };

    legend.addTo(map);
    return () => map.removeControl(legend);
  }, [map, type]);

  // Tell the user the month has no raster instead of just showing nothing --
  // an empty map used to be indistinguishable from a broken overlay.
  if (!rastersLoading && !available) {
    return (
      <div
        style={{
          direction: "rtl",
          position: "absolute",
          bottom: 110,
          right: 10,
          background: "#3e3a49a1",
          color: "#fff",
          padding: "6px 10px",
          borderRadius: 6,
          zIndex: 1000,
          fontSize: 13,
        }}
      >
        برای {period} نقشه‌ی رنگی موجود نیست
      </div>
    );
  }

  if (!tiffFile || !imageUrl || !imageBounds) return null;

  return (
    <>
      <div
        style={{
          direction: "rtl",
          position: "absolute",
          bottom: 110,
          right: 10,
          background: "#3e3a49a1",
          padding: "6px 12px",
          borderRadius: "6px",
          color: "#fff",
          fontSize: "14px",
          zIndex: 1000,
          userSelect: "none",
          minWidth: "180px",
          textAlign: "center",
          fontFamily: "MyFont",
          whiteSpace: "nowrap",
        }}
      >
        {hoverValue !== null && hoverLatLng ? (
          <>
            مقدار داده:{" "}
            {isNaN(hoverValue) || hoverValue < -1e5 || hoverValue > 1e5 ? (
              "مقداری وجود ندارد"
            ) : (
              <span
                style={{
                  unicodeBidi: "plaintext",
                  direction: "ltr",
                }}
              >
                {hoverValue.toFixed(1)}
              </span>
            )}
            <br />
            lat: {hoverLatLng.lat.toFixed(4)}, lon: {hoverLatLng.lng.toFixed(4)}
          </>
        ) : (
          "موس را روی نقشه حرکت دهید"
        )}
      </div>

      <div
        style={{
          position: "absolute",
          bottom: 50,
          right: 10,
          background: "#3e3a49a1",
          padding: "8px",
          borderRadius: "8px",
          zIndex: 1000,
          boxShadow: "0 0 5px rgba(0,0,0,0.2)",
          fontFamily: "MyFont",
        }}
      >
        <span style={{ marginRight: "8px", color: "white", fontSize: "14px" }}>
          شفافیت:{" "}
        </span>
        {[0.2, 0.4, 0.6, 0.8, 1].map((val) => (
          <button
            key={val}
            onClick={() => setOpacity(val)}
            style={{
              margin: "2px",
              padding: "4px 8px",
              backgroundColor: opacity === val ? "#f39c12" : "#3e3a49a1",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: opacity === val ? "bold" : "normal",
            }}
          >
            {val}
          </button>
        ))}
      </div>
    </>
  );
};

export default TiffProcessor;
